import type { OperationDetail } from '@/shared/api/resources';

/**
 * Uma tela por etapa na pagina da operacao (ADR 0107).
 *
 * O dono pediu, em 04/11/2026, que cada etapa da esteira tenha a sua tela, com as
 * opcoes daquela etapa, e nao todos os quadros na mesma pagina. Aqui fica o mapa de
 * etapa e quadro e as regras de navegacao entre as telas. A organizacao e so da tela:
 * cada quadro continua decidindo o que pode fazer pela etapa da operacao e pela
 * permissao, e o servidor confere tudo de novo.
 */

/** As partes do Resumo da cotacao, na ordem do OnPoint (`QuoteSummaryTab`). */
export const secoesDaCotacao = [
  'resumo',
  'modalidade',
  'coberturas',
  'clausulas',
  'objetos',
  'configuracao',
  'valores-por-alcada',
  'financeiro',
  'download-minuta',
] as const;

export type SecaoDaCotacao = (typeof secoesDaCotacao)[number];

/** As secoes da cotacao que gravam pelo "Atualizar cotacao". */
export const secoesDoFormulario: readonly SecaoDaCotacao[] = [
  'resumo',
  'modalidade',
  'configuracao',
  'valores-por-alcada',
];

/** Os quadros da pagina que nao sao do Resumo da cotacao. */
export const quadrosDaOperacao = [
  'credito',
  'subscricao',
  'juridico',
  'avalistas',
  'alcadas',
  'minuta',
  'ccg',
  'emissao',
] as const;

export type QuadroDaOperacao = (typeof quadrosDaOperacao)[number];

export type QuadroDaTela = SecaoDaCotacao | QuadroDaOperacao;

/**
 * O que cada etapa mostra, nesta ordem: primeiro o quadro que decide a etapa, depois o
 * que serve a ele. Um quadro que serve a duas etapas aparece nas duas. As secoes da
 * cotacao saem sempre na ordem do OnPoint, num bloco so (`blocosDaTela`).
 *
 * - Em cotacao tem o Resumo da cotacao inteiro. Valores por alcada e o plano
 *   financeiro mudam desde a cotacao (`cotacao.ts` e `InstallmentPlanEditable`), e a
 *   tela de uma etapa concluida sempre abre: com eles aqui, nenhum campo que o servidor
 *   aceita fica sem tela enquanto as etapas adiante estao travadas.
 * - As alcadas (`OperationAuthorityPanel`) entram nas tres decisoes que elas conferem:
 *   o limite de credito, a subscricao pela importancia segurada e a liberacao de
 *   impedimento, que barra a emissao.
 * - Os avalistas entram no credito e na CCG, que cita cada um pelo nome.
 * - Clausulas particulares, Objetos e o Download da minuta voltam no juridico, para a
 *   revisao do texto; os valores por alcada voltam no comercial, com o financeiro.
 * - As assinaturas ficam nos quadros Minuta e CCG: e la que a coleta e aberta e
 *   acompanhada, sobre o documento vigente (`GenerationPanel`).
 */
export const telasDaOperacao: Readonly<Record<string, readonly QuadroDaTela[]>> = {
  em_cotacao: [
    'resumo',
    'modalidade',
    'coberturas',
    'clausulas',
    'objetos',
    'configuracao',
    'valores-por-alcada',
    'financeiro',
    'download-minuta',
  ],
  aguardando_credito: ['credito', 'avalistas', 'alcadas'],
  em_subscricao: ['subscricao', 'valores-por-alcada', 'alcadas'],
  em_juridico: ['juridico', 'clausulas', 'objetos', 'download-minuta'],
  aguardando_comercial: ['valores-por-alcada', 'financeiro'],
  minuta_gerada: ['minuta', 'download-minuta'],
  aguardando_ccg: ['ccg', 'avalistas'],
  aguardando_assinatura: ['minuta', 'ccg'],
  em_emissao: ['emissao', 'alcadas'],
  emitida: ['emissao'],
};

/**
 * Os quadros da tela de uma etapa.
 *
 * Nenhum quadro some da pagina: o quadro cujas etapas nao vieram na progressao do
 * servidor entra na tela da primeira etapa. Etapa nova, sem quadro no mapa, abre a
 * tela so com a navegacao e o Proximo passo.
 */
export function quadrosDaTela(
  etapa: string,
  progressao: readonly string[],
): QuadroDaTela[] {
  const quadros = [...(telasDaOperacao[etapa] ?? [])];
  if (etapa !== progressao[0]) return quadros;
  const naProgressao = new Set(
    progressao.flatMap((item) => telasDaOperacao[item] ?? []),
  );
  for (const quadro of [...secoesDaCotacao, ...quadrosDaOperacao]) {
    if (!naProgressao.has(quadro)) quadros.push(quadro);
  }
  return quadros;
}

export type BlocoDaTela =
  | { tipo: 'cotacao'; secoes: SecaoDaCotacao[] }
  | { tipo: 'quadro'; quadro: QuadroDaOperacao };

/**
 * Junta as secoes da cotacao seguidas num bloco so: o Resumo da cotacao tem um
 * formulario e um "Atualizar cotacao" so, e duas copias na mesma tela gravariam cada
 * uma uma parte.
 */
export function blocosDaTela(quadros: readonly QuadroDaTela[]): BlocoDaTela[] {
  const blocos: BlocoDaTela[] = [];
  for (const quadro of quadros) {
    if (ehSecaoDaCotacao(quadro)) {
      const ultimo = blocos[blocos.length - 1];
      if (ultimo?.tipo === 'cotacao') ultimo.secoes.push(quadro);
      else blocos.push({ tipo: 'cotacao', secoes: [quadro] });
    } else {
      blocos.push({ tipo: 'quadro', quadro });
    }
  }
  return blocos;
}

function ehSecaoDaCotacao(quadro: QuadroDaTela): quadro is SecaoDaCotacao {
  return (secoesDaCotacao as readonly string[]).includes(quadro);
}

/**
 * Onde a esteira esta: a etapa atual ou, na operacao declinada, a etapa em que ela
 * parou, lida na linha do tempo. Sem esse registro, a primeira etapa.
 */
export function etapaDaEsteira(
  operacao: Pick<OperationDetail, 'stage' | 'progression' | 'timeline'>,
): string {
  const { stage, progression } = operacao;
  if (progression.includes(stage)) return stage;
  const declinio = [...operacao.timeline]
    .filter((item) => item.to_stage === stage && item.from_stage !== '')
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  if (declinio && progression.includes(declinio.from_stage)) {
    return declinio.from_stage;
  }
  return progression[0] ?? stage;
}

/**
 * A situacao da tela de uma etapa.
 *
 * - `concluida`: a esteira ja passou por ela. A tela abre, e o Continuar so navega.
 * - `atual`: a esteira esta nela. O Continuar e o movimento da esteira.
 * - `travada`: adiante da atual. Nao abre ate a esteira chegar.
 * - `encerrada`: a operacao chegou a uma etapa terminal. Todas as telas abrem, para
 *   leitura, e o Continuar so navega.
 */
export type SituacaoDaTela = 'concluida' | 'atual' | 'travada' | 'encerrada';

export function situacaoDaTela(
  etapa: string,
  operacao: Pick<OperationDetail, 'stage' | 'progression' | 'timeline' | 'is_terminal'>,
): SituacaoDaTela {
  if (operacao.is_terminal) return 'encerrada';
  const posicao = operacao.progression.indexOf(etapa);
  const atual = operacao.progression.indexOf(etapaDaEsteira(operacao));
  if (posicao === atual) return 'atual';
  return posicao >= 0 && posicao < atual ? 'concluida' : 'travada';
}

/** A dica da etapa travada, no indicador e na tela. */
export const dicaDaEtapaTravada = 'Conclua a etapa atual para seguir';

/**
 * A tela aberta: a pedida no endereco (?etapa=), quando ela existe e abre, ou a da
 * etapa em que a esteira esta.
 */
export function telaAberta(
  pedida: string | null,
  operacao: Pick<OperationDetail, 'stage' | 'progression' | 'timeline' | 'is_terminal'>,
): string {
  if (
    pedida &&
    operacao.progression.includes(pedida) &&
    situacaoDaTela(pedida, operacao) !== 'travada'
  ) {
    return pedida;
  }
  return etapaDaEsteira(operacao);
}

/** As telas vizinhas, para o Voltar e o Continuar. */
export function telasVizinhas(
  etapa: string,
  progressao: readonly string[],
): { anterior: string | undefined; seguinte: string | undefined } {
  const posicao = progressao.indexOf(etapa);
  if (posicao < 0) return { anterior: undefined, seguinte: undefined };
  return {
    anterior: progressao[posicao - 1],
    seguinte: progressao[posicao + 1],
  };
}

/**
 * O quadro de cada parecer exigido por um movimento (`requires`), para onde o aviso
 * do movimento travado leva: a ancora fica no quadro, em `AnalysisPanels`.
 */
export const quadroDoParecer: Readonly<
  Record<string, { quadro: QuadroDaOperacao; ancora: string; nome: string }>
> = {
  credito: { quadro: 'credito', ancora: 'parecer-credito', nome: 'Analise de credito' },
  subscricao: {
    quadro: 'subscricao',
    ancora: 'parecer-subscricao',
    nome: 'Subscricao',
  },
  juridico: {
    quadro: 'juridico',
    ancora: 'parecer-juridico',
    nome: 'Parecer juridico',
  },
};

/**
 * A tela em que um quadro aparece, para o atalho "Ir para ...": a da etapa preferida,
 * quando o quadro esta nela, ou a primeira da progressao que o mostra.
 */
export function telaDoQuadro(
  quadro: QuadroDaTela,
  preferida: string,
  progressao: readonly string[],
): string | undefined {
  if (quadrosDaTela(preferida, progressao).includes(quadro)) return preferida;
  return progressao.find((etapa) => quadrosDaTela(etapa, progressao).includes(quadro));
}
